import React, { useState, useEffect } from 'react';
import { Download, Upload, RefreshCw, Database, CheckCircle, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DataSync = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [importResult, setImportResult] = useState(null);

  const fetchCollections = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/data/collections`);
      const data = await res.json();
      setCollections(data.collections || []);
      setSelectedCollections(data.collections?.map(c => c.name) || []);
    } catch (err) {
      console.error('Error fetching collections:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCollections();
  }, []);

  const handleExport = async () => {
    setExporting(true);
    try {
      const collectionsParam = selectedCollections.join(',');
      const res = await fetch(`${API_URL}/api/admin/data/export?collections=${collectionsParam}`);
      const data = await res.json();
      
      // Download as JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fwd_export_${new Date().toISOString().slice(0,10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      alert('✅ Export downloaded successfully!');
    } catch (err) {
      console.error('Export error:', err);
      alert('❌ Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await fetch(`${API_URL}/api/admin/data/import?mode=${importMode}`, {
        method: 'POST',
        body: formData
      });
      
      const result = await res.json();
      
      if (res.ok) {
        setImportResult(result);
        fetchCollections(); // Refresh counts
        alert('✅ Import completed successfully!');
      } else {
        alert('❌ Import failed: ' + (result.detail || 'Unknown error'));
      }
    } catch (err) {
      console.error('Import error:', err);
      alert('❌ Import failed');
    } finally {
      setImporting(false);
      e.target.value = ''; // Reset file input
    }
  };

  const toggleCollection = (name) => {
    setSelectedCollections(prev => 
      prev.includes(name) 
        ? prev.filter(c => c !== name)
        : [...prev, name]
    );
  };

  const selectAll = () => {
    setSelectedCollections(collections.map(c => c.name));
  };

  const selectNone = () => {
    setSelectedCollections([]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading collections...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">🔄 Data Sync</h1>
          <p className="text-gray-600 text-sm mt-1">
            Export and import content between Preview and Production environments
          </p>
        </div>
        <button
          onClick={fetchCollections}
          disabled={loading}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <h3 className="font-semibold text-blue-800 mb-2">ℹ️ How to sync data between environments:</h3>
        <ol className="text-blue-700 text-sm space-y-1 list-decimal list-inside">
          <li><strong>Export</strong> data from the source environment (e.g., Preview)</li>
          <li><strong>Download</strong> the JSON file</li>
          <li>Go to the <strong>target environment</strong> (e.g., Production)</li>
          <li><strong>Import</strong> the JSON file there</li>
        </ol>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Export Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Download className="w-5 h-5 text-teal-600" />
            Export Data
          </h2>
          
          <p className="text-gray-600 text-sm mb-4">
            Select collections to export:
          </p>

          <div className="flex gap-2 mb-3">
            <button 
              onClick={selectAll}
              className="text-sm text-teal-600 hover:underline"
            >
              Select All
            </button>
            <span className="text-gray-300">|</span>
            <button 
              onClick={selectNone}
              className="text-sm text-teal-600 hover:underline"
            >
              Select None
            </button>
          </div>

          <div className="space-y-2 mb-6 max-h-64 overflow-y-auto">
            {collections.map((col) => (
              <label 
                key={col.name}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={selectedCollections.includes(col.name)}
                  onChange={() => toggleCollection(col.name)}
                  className="w-4 h-4 text-teal-600 rounded"
                />
                <Database className="w-4 h-4 text-gray-400" />
                <span className="flex-1 text-gray-700">{col.name}</span>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                  {col.count} items
                </span>
              </label>
            ))}
          </div>

          <button
            onClick={handleExport}
            disabled={exporting || selectedCollections.length === 0}
            className="w-full py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {exporting ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-5 h-5" />
                Export Selected ({selectedCollections.length})
              </>
            )}
          </button>
        </div>

        {/* Import Section */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Upload className="w-5 h-5 text-blue-600" />
            Import Data
          </h2>

          <p className="text-gray-600 text-sm mb-4">
            Import mode:
          </p>

          <div className="space-y-2 mb-6">
            <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
              <input
                type="radio"
                name="importMode"
                value="merge"
                checked={importMode === 'merge'}
                onChange={(e) => setImportMode(e.target.value)}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-800">Merge (Safe)</div>
                <div className="text-sm text-gray-500">Add new items, skip existing ones</div>
              </div>
            </label>
            
            <label className="flex items-start gap-3 p-3 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer">
              <input
                type="radio"
                name="importMode"
                value="update"
                checked={importMode === 'update'}
                onChange={(e) => setImportMode(e.target.value)}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-gray-800">Update</div>
                <div className="text-sm text-gray-500">Update existing items, add new ones</div>
              </div>
            </label>
            
            <label className="flex items-start gap-3 p-3 rounded-lg border border-orange-200 bg-orange-50 hover:bg-orange-100 cursor-pointer">
              <input
                type="radio"
                name="importMode"
                value="replace"
                checked={importMode === 'replace'}
                onChange={(e) => setImportMode(e.target.value)}
                className="mt-1"
              />
              <div>
                <div className="font-medium text-orange-800">Replace (Destructive)</div>
                <div className="text-sm text-orange-600">Delete all existing, import fresh</div>
              </div>
            </label>
          </div>

          <label className="block">
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              disabled={importing}
              className="hidden"
            />
            <div className={`w-full py-3 rounded-lg flex items-center justify-center gap-2 cursor-pointer border-2 border-dashed ${importing ? 'border-gray-300 bg-gray-50 text-gray-400' : 'border-blue-300 bg-blue-50 text-blue-600 hover:bg-blue-100'}`}>
              {importing ? (
                <>
                  <RefreshCw className="w-5 h-5 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  Choose JSON File to Import
                </>
              )}
            </div>
          </label>
        </div>
      </div>

      {/* Import Results */}
      {importResult && (
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Import Results
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(importResult.collections || {}).map(([name, result]) => (
              <div key={name} className="p-3 bg-gray-50 rounded-lg">
                <div className="font-medium text-gray-800 text-sm">{name}</div>
                {result.status === 'success' ? (
                  <div className="text-xs text-gray-500 mt-1">
                    {result.imported > 0 && <span className="text-green-600">+{result.imported} new</span>}
                    {result.updated > 0 && <span className="text-blue-600 ml-2">{result.updated} updated</span>}
                    {result.skipped > 0 && <span className="text-gray-400 ml-2">{result.skipped} skipped</span>}
                  </div>
                ) : (
                  <div className="text-xs text-orange-600 mt-1">{result.reason || result.status}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Warning */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
        <div>
          <h3 className="font-semibold text-yellow-800">Important Notes</h3>
          <ul className="text-yellow-700 text-sm mt-1 list-disc list-inside">
            <li>Export from Preview, then import on Production (or vice versa)</li>
            <li>Use "Merge" mode to safely add new content without overwriting</li>
            <li>Use "Replace" mode only if you want to fully sync (deletes existing!)</li>
            <li>Images/files are stored separately and won't be synced</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DataSync;
